import React from 'react';
import { action } from 'mobx';
import { observer } from 'mobx-react';
import { useParams, useHistory, useRouteMatch } from 'react-router-dom';
import { debounce } from 'throttle-debounce';

import { ModalStore } from 'ui/base/modal';
import { CorePage } from 'ui/base/page/core_page';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';
import { useOnScreen } from 'ui/base/screen';
import { ToastID, defaultToast } from 'ui/base/toast';

import { PangolinSelector } from 'ui/standard/pangolin_selector/pangolin_selector';

import { ResultKind, ServiceStatus } from 'services/base/types';
import { BasePathItems, BasePath, pagePathToPathString } from 'services/base/page_path';
import { togglePullToRefresh } from 'services/base/ipc';
import { GlobalLocaleStore, localeToLanguageCode } from 'services/base/i18n';

import { Pangolin, PlanFirewallPackage } from 'services/accounts/types';
import { useAccountsDomain } from 'services/accounts/accounts_domain';
import { authorizePlanFirewall } from 'services/accounts/logic/plans/plan_firewall';

import { IPangolinDomain } from 'services/pangolin/types';
import { usePangolinDomain } from 'services/pangolin/pangolin_domain';
import { IssuesService } from 'services/pangolin/issues_service';

import { Redirect } from 'pages/core/redirect/redirect';

import { IssueItem, IssueProgress } from './ui/issue_item/issue_item';

import { FilterIssueModal } from './modals/filter_issue/filter_issue';
import { ViewIssueModal } from './modals/view_issue/view_issue';

import baseStyles from './list_issues.module.css';


type ListIssuesStyles = {
  container: string;
  header: string;
  headerItem: string;
  headerButton: string;
  headerButtonIcon: string;
  content: string;
  items: string;
  pangolinName: string;
  safeContent: string;
  safeNoThreatsImage: string;
  safeSubtitleText: string;
  safeText: string;
};

const themedStyles: ThemedStyles<ListIssuesStyles> = {
  [Theme.DAYLIGHT]: {
    container: baseStyles.container,
    header: baseStyles.daylightHeader,
    headerItem: baseStyles.headerItem,
    headerButton: baseStyles.headerButton,
    headerButtonIcon: baseStyles.daylightHeaderButtonIcon,
    content: baseStyles.content,
    items: baseStyles.items,
    pangolinName: baseStyles.pangolinName,
    safeContent: baseStyles.safeContent,
    safeNoThreatsImage: baseStyles.daylightSafeNoThreatsImage,
    safeSubtitleText: baseStyles.daylightSafeSubtitleText,
    safeText: baseStyles.daylightSafeText,
  },
  [Theme.MIDNIGHT]: {
    container: baseStyles.container,
    header: baseStyles.midnightHeader,
    headerItem: baseStyles.headerItem,
    headerButton: baseStyles.headerButton,
    headerButtonIcon: baseStyles.midnightHeaderButtonIcon,
    content: baseStyles.content,
    items: baseStyles.items,
    pangolinName: baseStyles.pangolinName,
    safeContent: baseStyles.safeContent,
    safeNoThreatsImage: baseStyles.midnightSafeNoThreatsImage,
    safeSubtitleText: baseStyles.midnightSafeSubtitleText,
    safeText: baseStyles.midnightSafeText,
  },
};

const doDebouncedFetchPage = debounce(1000, (domain: IPangolinDomain, pangolin: Pangolin) => {
  IssuesService.fetchPage(domain, pangolin).then(result => {
    if (result.kind !== ResultKind.OK) {
      if (!pangolin.onlineStatus) {
        defaultToast(ToastID.ERROR_QUERY_PANGOLIN_OFFLINE);
      } else if (result.status === 402) {
        defaultToast(ToastID.ERROR_QUERY_PLAN_REQUIRED);
      } else {
        defaultToast(ToastID.ERROR_QUERY_GENERIC);
      }
    }
  });
});

const doDebouncedSetValue = debounce(50, (setValue, value) => {
  setValue(value);
});

// const SafeContent = observer(() => {
//   const styles = useStyles(themedStyles);
//   return (
//     <div className={styles.safeContent}>
//       <div className={styles.safeNoThreatsImage} />
//       <div className={styles.safeSubtitleText}>{Messages.safeTitle()}</div>
//       <p className={styles.safeText}>{Messages.safeBlurb()}</p>
//     </div>
//   );
// });

type IssuesLoaderProps = {
  pangolin: Pangolin;
  domain: IPangolinDomain;
};
const IssuesLoader = observer(({ pangolin, domain }: IssuesLoaderProps) => {
  const ref = React.useRef<HTMLDivElement>(null);

  const online = pangolin.onlineStatus;
  const capped = domain.issuesCapped;
  const status = domain.issuesStatus;
  const failed = status === ServiceStatus.FAILED;
  const loading = status === ServiceStatus.LOADING;

  const visible = useOnScreen(ref);
  const empty = domain.issues.size === 0;

  const trigger = visible || empty;

  const fetch = online && !capped && !failed && !loading && trigger;
  React.useEffect(() => {
    if (fetch) doDebouncedFetchPage(domain, pangolin);
  }, [fetch, domain, pangolin]);

  return <div ref={ref} />;
});

type IssueItemsContainerProps = {
  pangolin: Pangolin;
  domain: IPangolinDomain;
};
const IssueItemsContainer = observer(({ pangolin, domain }: IssueItemsContainerProps) => {
  const styles = useStyles(themedStyles);

  const locale = localeToLanguageCode(GlobalLocaleStore.get());
  const format: object = {
    month: 'short',
    year: 'numeric',
    day: 'numeric',
    weekday: 'short',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
  };

  const capped = domain.issuesCapped;
  const status = domain.issuesStatus;
  const failed = status === ServiceStatus.FAILED;
  const loading = status === ServiceStatus.LOADING;

  React.useEffect(() => {
    if (!capped && !failed) {
      document.getAnimations().forEach((animation) => {
        animation.cancel();
        animation.play();
      });
    }
  }, [capped, failed]);

  return (
    <div className={styles.items}>
      {
        domain.issues.map((item, id) =>
          <IssueItem
            key={`item-${id}`}
            domain={domain}
            pangolin={pangolin}
            pair={[id, item]}
            locale={locale}
            format={format}
          />
        )
      }
      {
        !loading ?
          <IssuesLoader
            domain={domain}
            pangolin={pangolin}
          /> : null
      }
      {
        !capped && !failed ? <>
          <IssueProgress
            locale={locale}
            format={format}
          />
          <IssueProgress
            locale={locale}
            format={format}
          />
        </> : null
      }
    </div>
  );
});

type BaseListIssuesProps = {
  pangolin: Pangolin;
  domain: IPangolinDomain;
};
export const BaseListIssues = observer(({ pangolin, domain }: BaseListIssuesProps) => {
  const history = useHistory();
  const styles = useStyles(themedStyles);

  const filterItemsMatch = useRouteMatch(pagePathToPathString({
    kind: BasePath.LIST_ISSUES,
    pangolinID: pangolin.id,
    itemsPath: {
      kind: BasePathItems.FILTER_ITEMS,
    },
  }));

  const viewItemMatch = useRouteMatch<{itemID: string}>(
    pagePathToPathString({
      kind: BasePath.LIST_ISSUES,
      pangolinID: pangolin.id,
      itemsPath: {
        kind: BasePathItems.VIEW_ITEM,
        itemID: ':itemID',
      },
    })
  );

  const onShowFilterModal = React.useCallback(action(() => {
    if (authorizePlanFirewall(pangolin, PlanFirewallPackage.BASIC)) {
      history.push(pagePathToPathString({
        kind: BasePath.LIST_ISSUES,
        pangolinID: pangolin.id,
        itemsPath: {
          kind: BasePathItems.FILTER_ITEMS,
        },
      }));
    } else {
      defaultToast(ToastID.ERROR_ACTION_PLAN_REQUIRED);
    }
  }), []);

  React.useEffect(() => {
    if (domain.issuesFiltered) {
      domain.issuesFiltered = false;
      IssuesService.clearData(domain);
    } else {
      IssuesService.clearAll(domain);
    }
    return () => {
      IssuesService.clearAll(domain);
    };
  }, [domain]);

  const [filterModalStore] = React.useState(new ModalStore(false));
  const [viewModalStore] = React.useState(new ModalStore(false));

  const [isScrollOnTop, setScrollOnTop] = React.useState(true);
  const handleScroll = React.useCallback((event: React.UIEvent<HTMLElement>) => {
    const scrollOnTop = event.currentTarget.scrollTop === 0;
    doDebouncedSetValue(setScrollOnTop, scrollOnTop);
  }, []);

  React.useEffect(() => {
    const isModalHidden = !filterModalStore.visible && !viewModalStore.visible;
    if (!isScrollOnTop || !isModalHidden) togglePullToRefresh(false);
    else togglePullToRefresh(true, () => { IssuesService.clearDate(domain); });
    return () => { togglePullToRefresh(false); };
  }, [domain, isScrollOnTop, filterModalStore.visible, viewModalStore.visible]);

  React.useEffect(() => {
    const view = !!filterItemsMatch;
    if (view) filterModalStore.showModal();
    else filterModalStore.hideModal();
  }, [filterItemsMatch, filterModalStore]);

  React.useEffect(() => {
    if (viewItemMatch) {
      const { itemID } = viewItemMatch.params;

      const view = itemID && domain.issuesItem;
      if (view) viewModalStore.showModal();
      else viewModalStore.hideModal();

      const gone = itemID && !domain.issuesItem;
      if (gone) {
        history.replace(pagePathToPathString({
          kind: BasePath.LIST_ISSUES,
          pangolinID: pangolin.id,
        }));
      }
    } else {
      viewModalStore.hideModal();
    }
  }, [viewItemMatch, viewModalStore, pangolin.id, domain.issuesItem, history]);

  return (
    <>
      <FilterIssueModal
        domain={domain}
        modal={filterModalStore}
      />
      <ViewIssueModal
        domain={domain}
        modal={viewModalStore}
      />
      <CorePage pangolin={pangolin} location={BasePath.LIST_ISSUES}>
        <div className={styles.container}>
          <div className={styles.header}>
            <div className={styles.headerItem}>
              <PangolinSelector pangolin={pangolin} location={BasePath.LIST_ISSUES}>
                <div className={styles.pangolinName}>
                  {pangolin.nickname}
                </div>
              </PangolinSelector>
              <button type='button' className={styles.headerButton}
                      onClick={onShowFilterModal}>
                <div className={styles.headerButtonIcon}/>
              </button>
            </div>
          </div>
          <div className={styles.content} onScroll={handleScroll}>
            {
              pangolin.onlineStatus ?
                <IssueItemsContainer
                  domain={domain}
                  pangolin={pangolin}
                /> : null
            }
          </div>
        </div>
      </CorePage>
    </>
  );
});

export const ListIssues = observer(() => {
  const { pangolinID } = useParams<{ pangolinID: string }>();
  if (!pangolinID) return <Redirect />;

  const accounts = useAccountsDomain();

  const pangolin = accounts.pangolins.get(pangolinID);
  if (!pangolin) return <Redirect />;

  const domain = usePangolinDomain(pangolin.id);
  if (!domain) return <Redirect />;

  return (
    <BaseListIssues
      pangolin={pangolin}
      domain={domain}
    />
  );
});
